/**
 * SDK 简化房型卡(B6) — 设计图样式版
 *
 * 不复用 hotel-hub RoomCard(依赖 ImageCarousel/embla、RoomDetailModal/Radix、RatePlanItem 等)。
 * 这里实现够用形态:单图 + 房型名 + 床型 + 套餐项(标题/标签/价格/立即预订按钮)+ 展开收起。
 * 复制 getPackageStatus/getMealInfo/getCancellationInfo 三纯函数(来自 package-helpers,无运行时依赖)。
 */
import { useEffect, useRef, useState } from 'react';
import type { RoomType, PackageItem } from '@/types/room';
import { useMediaQuery } from '@/ui/hooks/useMediaQuery';
import { RoomDetailModal } from './RoomDetailModal';

// ===== 复制自 src/components/packages/package-helpers.ts(纯函数) =====
type PackageStatus = 'available' | 'dateRestriction' | 'unavailable';
function getPackageStatus(pkg: PackageItem): PackageStatus {
  if (pkg.sellState !== 1 && (!pkg.isViewCalendar || pkg.isSellOut)) return 'unavailable';
  if (pkg.isViewCalendar) return 'dateRestriction';
  return 'available';
}
function getMealInfo(pkg: PackageItem): string | undefined {
  if (pkg.breakfastType === 2 && pkg.breakfastNum > 0) return `${pkg.breakfastNum}份早餐`;
  return undefined;
}
interface CancellationInfo {
  type: 'nonRefundable' | 'freeCancellation';
  rawPolicy: string;
}
function getCancellationInfo(pkg: PackageItem): CancellationInfo {
  if (pkg.cancelType === 2) {
    return { type: 'freeCancellation', rawPolicy: pkg.cancelPolicy };
  }
  return { type: 'nonRefundable', rawPolicy: pkg.cancelPolicy };
}
// ===== end 复制 =====

interface RoomCardProps {
  room: RoomType;
  onBook: (pkg: PackageItem) => void;
}

const DEFAULT_VISIBLE_PLANS = 2;
/** 左侧图片轮播最多展示张数(对齐主站房卡 room.images.slice(0,5)) */
const MAX_CAROUSEL_IMAGES = 5;
/** H5 弹窗退出动画时长(与 styles.h5.ts 滑出动画一致);PC 瞬时卸载无需 */
const CLOSE_ANIM_MS = 220;

/** 早餐 icon:与主站套餐列表页(RatePlanItem)一致的 Utensils 刀叉图标 */
const UtensilsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
    <path d="M7 2v20" />
    <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
  </svg>
);

/** 无早 icon:与主站套餐列表页一致的 Ban 禁止圈图标 */
const BanIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m4.9 4.9 14.2 14.2" />
  </svg>
);

export function RoomCard({ room, onBook }: RoomCardProps) {
  const [showAll, setShowAll] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  // 弹窗实际挂载态:与 detailOpen 解耦,H5 关闭时延迟卸载以播退出动画,PC 瞬时卸载
  const [modalMounted, setModalMounted] = useState(false);
  const hasFacilities = room.facilities.length > 0;
  const isMobile = useMediaQuery('(max-width: 640px)', false);
  const modalCloseTimer = useRef<number | undefined>(undefined);

  const openDetail = () => {
    window.clearTimeout(modalCloseTimer.current);
    setDetailOpen(true);
    setModalMounted(true);
  };
  const closeDetail = () => {
    setDetailOpen(false);
    if (!isMobile) {
      // PC 瞬时卸载(对齐主流程桌面 if(!isOpen) return null)
      setModalMounted(false);
      return;
    }
    // H5 播滑出动画后卸载(退出动画期间维持挂载)
    modalCloseTimer.current = window.setTimeout(() => setModalMounted(false), CLOSE_ANIM_MS);
  };

  useEffect(() => () => window.clearTimeout(modalCloseTimer.current), []);

  // 弹窗(RoomDetailModal)自行 portal 到 document.body 级独立 shadow,遮罩在根层叠上下文
  // 盖住挂 body 的第三方挂件(如客服 widget.js);无需在此提升 shadow host 的 z-index。
  const plans = room.packages;
  const visiblePlans = showAll ? plans : plans.slice(0, DEFAULT_VISIBLE_PLANS);
  const hasHiddenPlans = plans.length > DEFAULT_VISIBLE_PLANS;
  const isSinglePlan = plans.length === 1;

  // 左侧图片轮播:对齐主站套餐列表房卡(room.images.slice(0,5) + 左右箭头 + 底部圆点 + 循环)。
  // SDK 独立 bundle 不引 embla,用 state + translateX 实现等价轻量轮播;
  // H5(≤640px)由 CSS 隐藏箭头/圆点、退化为 96×96 单图(见 styles.h5.ts)。
  const carouselImages = room.images.slice(0, MAX_CAROUSEL_IMAGES);
  const hasMultipleImages = carouselImages.length > 1;
  const goPrevImage = () =>
    setImgIndex((i) => (i - 1 + carouselImages.length) % carouselImages.length);
  const goNextImage = () => setImgIndex((i) => (i + 1) % carouselImages.length);

  return (
    <section className="hh-room">
      {hasFacilities ? (
        <button
          type="button"
          className="hh-room__name hh-room__name--link"
          onClick={openDetail}
          aria-label="查看房间详情"
        >
          <span>{room.name}</span>
          <svg
            className="hh-room__name-arrow"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      ) : (
        <h2 className="hh-room__name">{room.name}</h2>
      )}
      <div className="hh-room__body">
        <div className="hh-room__media">
          {carouselImages.length > 0 ? (
            <div className="hh-room__carousel">
              <div
                className="hh-room__carousel-track"
                style={{ transform: `translateX(-${imgIndex * 100}%)` }}
              >
                {carouselImages.map((img, i) => (
                  // SDK 独立 bundle 脱离 Next 运行时,无法用 next/image,用原生 img
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={i}
                    className="hh-room__img"
                    src={img.src}
                    alt={img.alt || room.name}
                    draggable={false}
                  />
                ))}
              </div>

              {hasMultipleImages && (
                <>
                  <button
                    type="button"
                    className="hh-room__nav hh-room__nav--prev"
                    aria-label="上一张"
                    onClick={goPrevImage}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="hh-room__nav hh-room__nav--next"
                    aria-label="下一张"
                    onClick={goNextImage}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </button>
                  <div className="hh-room__dots">
                    {carouselImages.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`hh-room__dot${i === imgIndex ? 'hh-room__dot--active' : ''}`}
                        aria-label={`第 ${i + 1} 张`}
                        aria-current={i === imgIndex ? 'true' : undefined}
                        onClick={() => setImgIndex(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="hh-room__carousel" />
          )}
        </div>

        <div className="hh-room__plans">
          {visiblePlans.map((plan) => {
            const status = getPackageStatus(plan);
            const available = status === 'available';
            const meal = getMealInfo(plan);
            const hasBreakfast = Boolean(meal);
            const mealText = meal ?? '无早';
            const cancellation = getCancellationInfo(plan);
            // packageType 非 1（非普通售卖套餐）不显示取消政策
            const showCancellation = plan.packageType == null || plan.packageType === 1;
            const hasCoupon = (plan.discountAmount ?? 0) > 0 && plan.afterCouponPrice != null;
            const currentPrice = hasCoupon ? plan.afterCouponPrice : plan.price;

            return (
              <div
                className={`hh-plan ${isSinglePlan ? 'hh-plan--single' : 'hh-plan--multiple'}`}
                key={plan.id}
              >
                <p className="hh-plan__brief" title={plan.brief || plan.name}>
                  {plan.brief || plan.name}
                </p>

                <div className="hh-plan__body">
                  <div className="hh-plan__left">
                    <div className="hh-plan__tags">
                      {/* packageType === 18 不展示早餐信息 */}
                      {plan.packageType !== 18 && (
                        <span
                          className={`hh-plan__tag hh-plan__tag--plain ${hasBreakfast ? 'hh-plan__tag--breakfast' : ''}`}
                        >
                          {hasBreakfast ? <UtensilsIcon /> : <BanIcon />} {mealText}
                        </span>
                      )}
                      {showCancellation &&
                        (cancellation.type === 'nonRefundable' ? (
                          <span className="hh-plan__tag hh-plan__tag--danger">不可取消</span>
                        ) : cancellation.rawPolicy ? (
                          <span className="hh-plan__tag hh-plan__tag--success">
                            {cancellation.rawPolicy}
                          </span>
                        ) : null)}
                    </div>
                    {/* {status === 'dateRestriction' && plan.minimumDescription && (
                      <div className="hh-plan__hint">{plan.minimumDescription}</div>
                    )} */}
                  </div>

                  <div className="hh-plan__right">
                    <div className="hh-plan__price">
                      <div className="hh-plan__price-main">
                        {hasCoupon ? (
                          <>
                            <span className="hh-plan__price-badge">券后价</span>
                            <span className="hh-plan__price-val">
                              <span className="hh-plan__price-symbol">¥</span>
                              <span className="hh-plan__price-num">
                                <span className="hh-plan__price-unit">每间每晚</span>
                                {currentPrice}
                              </span>
                            </span>
                            <span className="hh-plan__price-original">¥{plan.price}</span>
                          </>
                        ) : (
                          <span className="hh-plan__price-val">
                            <span className="hh-plan__price-symbol">¥</span>
                            <span className="hh-plan__price-num">
                              <span className="hh-plan__price-unit">每间每晚</span>
                              {currentPrice}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="hh-plan__book"
                      disabled={!available}
                      onClick={() => available && onBook(plan)}
                    >
                      {available ? '立即预订' : '不可订'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {hasHiddenPlans && (
            <button
              type="button"
              className="hh-plan__toggle"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
            >
              <span>{showAll ? '收起' : '更多套餐'}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={showAll ? 'hh-plan__toggle-icon--up' : 'hh-plan__toggle-icon--down'}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {modalMounted && hasFacilities && (
        <RoomDetailModal room={room} isOpen={detailOpen} onClose={closeDetail} />
      )}
    </section>
  );
}
